import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useWallet } from "../../contexts/WalletContext";
import { useAuth } from "../../contexts/AuthContext";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { getTokenPairs, getPairByAddress } from "../../services/dexscreener-api";
import { importPrivateKey, removePrivateKey, hasPrivateKey } from "../../services/trading-api";
import WalletManager from "../wallet/WalletManager";
import AuthModal from "../auth/AuthModal";

const TransparentHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { publicKey, connected } = useWallet();
  const { user, isUnlocked, signOut: handleSignOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [showWalletManager, setShowWalletManager] = useState(false);
  const [walletManagerMode, setWalletManagerMode] = useState('manage'); // 'manage' or 'import'
  const [privateKeyInput, setPrivateKeyInput] = useState("");
  const [hasStoredPrivateKey, setHasStoredPrivateKey] = useState(false);
  const [isImportingKey, setIsImportingKey] = useState(false);
  const [keyStatusMessage, setKeyStatusMessage] = useState(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const chainId = searchParams.get("chain") || "solana"; // Get chain from URL or default to solana

  // Auth state is now managed by AuthContext
  const isAuthenticated = !!user;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isProfileDropdownOpen]);

  // Check if a private key is stored for the connected wallet
  useEffect(() => {
    let isMounted = true;
    
    const checkKeyStatus = async () => {
      if (!isMounted) return;
      
      if (publicKey && connected) {
        const hasKey = await hasPrivateKey(publicKey.toString());
        if (isMounted) {
          setHasStoredPrivateKey(hasKey);
        }
      } else {
        if (isMounted) {
          setHasStoredPrivateKey(false);
        }
      }
    };
    
    checkKeyStatus();
    // Refresh every 10 seconds
    const interval = setInterval(checkKeyStatus, 10000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [publicKey, connected]);

  // Detect scroll to add blur effect (throttled to prevent glitching)
  useEffect(() => {
    let ticking = false;
    let lastScrollPosition = 0;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY || document.documentElement.scrollTop || 0;
          
          // Only update state if scroll position actually changed (prevents unnecessary re-renders)
          if (Math.abs(scrollPosition - lastScrollPosition) > 0.5) {
            const shouldBeScrolled = scrollPosition > 10;
            // Only update if state would actually change (prevents rapid toggling)
            setIsScrolled(prev => prev !== shouldBeScrolled ? shouldBeScrolled : prev);
            lastScrollPosition = scrollPosition;
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    // Check initial scroll position
    const initialScroll = window.scrollY || document.documentElement.scrollTop || 0;
    setIsScrolled(initialScroll > 10);
    lastScrollPosition = initialScroll;
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Tokens", path: "/tokens" },
    { label: "Analytics", path: "/analytics" },
  ];

  const getChainPath = (chainId) => {
    const chainPathMap = {
      ethereum: "0x1",
      binance: "0x38",
      bsc: "0x38",
      polygon: "0x89",
      solana: "solana",
      arbitrum: "0xa4b1",
      base: "0x2105",
      avalanche: "0xa86a",
      optimism: "0xa",
      linea: "0xe708",
      fantom: "0xfa",
      pulse: "0x171",
      ronin: "0x7e4",
    };
    return chainPathMap[chainId] || chainId;
  };

  const isValidAddress = (address) => {
    if (!address) return false;
    const trimmed = address.trim();
    
    // Solana addresses are Base58, typically 32-44 characters
    if (chainId === "solana") {
      return trimmed.length >= 32 && trimmed.length <= 44 && /^[A-Za-z0-9]+$/.test(trimmed);
    }
    
    // EVM addresses start with 0x and are 42 characters
    return trimmed.startsWith('0x') && trimmed.length === 42;
  };

  const handleSearch = async () => {
    const input = searchValue.trim();
    
    if (!input) {
      setIsSearchOpen(true);
      return;
    }

    setIsSearching(true);

    try {
      const chainPath = getChainPath(chainId);
      let tokenAddress = null;

      // Check if it's a valid address format
      if (isValidAddress(input)) {
        // Try as contract address first (most common case)
        try {
          const pairs = await getTokenPairs(chainId, input);
          if (pairs && pairs.length > 0) {
            tokenAddress = input;
          } else {
            throw new Error('No pairs found for this address');
          }
        } catch (tokenErr) {
          // Not a token address, try as pair address
          try {
            const pairData = await getPairByAddress(chainId, input);
            if (pairData && pairData.pairs && pairData.pairs.length > 0) {
              const pair = pairData.pairs[0];
              tokenAddress = pair.baseToken?.address || pair.quoteToken?.address;
              if (!tokenAddress) {
                throw new Error('Could not extract token address from pair');
              }
            } else {
              throw new Error('Pair not found');
            }
          } catch (pairErr) {
            // Still try to navigate - let the token page handle the error
            tokenAddress = input;
          }
        }
      } else {
        // Not a valid address format
        setIsSearching(false);
        return;
      }

      if (tokenAddress) {
        navigate(`/${chainPath}/${tokenAddress}`);
        setSearchValue('');
        setIsSearchOpen(false);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
    if (e.key === 'Escape') {
      setIsSearchOpen(false);
      setSearchValue('');
    }
  };


  // Sign out handler
  const handleSignOutClick = async () => {
    await handleSignOut();
    setIsProfileDropdownOpen(false);
  };

  const handleImportPrivateKey = async () => {
    // For authenticated users, use vault; otherwise require Phantom connection
    const walletAddress = publicKey ? publicKey.toString() : null;
    
    if (!walletAddress) {
      setKeyStatusMessage({ type: "error", text: "Please connect your wallet first." });
      return;
    }
    
    if (!isUnlocked) {
      setKeyStatusMessage({ type: "error", text: "Please sign in and unlock your vault first." });
      setShowAuthModal(true);
      return;
    }
    if (!privateKeyInput.trim()) {
      setKeyStatusMessage({ type: "error", text: "Please enter your private key." });
      return;
    }

    setIsImportingKey(true);
    setKeyStatusMessage(null);

    try {
      const success = await importPrivateKey(walletAddress, privateKeyInput.trim());
      if (success) {
        setKeyStatusMessage({ type: "success", text: "Private key imported successfully!" });
        
        // Private key imported successfully
          setHasStoredPrivateKey(true);
        
        setShowPrivateKeyModal(false);
        setPrivateKeyInput("");
        setTimeout(() => setKeyStatusMessage(null), 3000);
      } else {
        setKeyStatusMessage({ type: "error", text: "Failed to import private key. Please check the key and try again." });
      }
    } catch (err) {
      console.error("Error importing private key:", err);
      setKeyStatusMessage({ type: "error", text: err.message || "Failed to import private key." });
    } finally {
      setIsImportingKey(false);
    }
  };

  const handleRemovePrivateKey = async () => {
    if (!publicKey) {
      setKeyStatusMessage({ type: "error", text: "Please connect your wallet first." });
      return;
    }

    if (window.confirm("Are you sure you want to remove your stored private key? This will disable backend signing.")) {
      try {
        const success = await removePrivateKey(publicKey.toString());
        if (success) {
          setKeyStatusMessage({ type: "success", text: "Private key removed successfully." });
          setHasStoredPrivateKey(false);
          setTimeout(() => setKeyStatusMessage(null), 3000);
        } else {
          setKeyStatusMessage({ type: "error", text: "Failed to remove private key." });
        }
      } catch (err) {
        console.error("Error removing private key:", err);
        setKeyStatusMessage({ type: "error", text: err.message || "Failed to remove private key." });
      }
    }
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 w-full"
      style={{
        borderBottom: '1px solid rgba(107, 114, 128, 0.3)'
      }}
    >
      {/* Transparent header - Fully transparent on load, blur on scroll */}
      <div 
        className={`transition-all duration-300 ${
          isScrolled 
            ? "bg-white/5 backdrop-blur-lg" 
            : "bg-transparent backdrop-blur-0"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group relative lg:-ml-5">
              <div className="relative h-8 lg:h-10">
                {/* Spacer image to set dimensions */}
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/trayd-logo.png`}
                  alt=""
                  className="h-8 lg:h-10 w-auto object-contain opacity-0 pointer-events-none"
                  aria-hidden="true"
                />
                {/* White logo - visible when not scrolled */}
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/trayd-logo.png`}
                  alt="Trayd"
                  className={`h-8 lg:h-10 w-auto object-contain absolute top-0 left-0 transition-opacity duration-300 ease-in-out ${
                    isScrolled 
                      ? 'opacity-0 pointer-events-none' 
                      : 'opacity-100 group-hover:opacity-80'
                  }`}
                />
                {/* Black logo - visible when scrolled */}
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/black-trayd-logo.png`}
                  alt="Trayd"
                  className={`h-8 lg:h-10 w-auto object-contain absolute top-0 left-0 transition-opacity duration-300 ease-in-out ${
                    isScrolled 
                      ? 'opacity-100 group-hover:opacity-80' 
                      : 'opacity-0 pointer-events-none'
                  }`}
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8 flex-1 justify-center lg:justify-start lg:ml-12">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-sm font-inter font-medium transition-all relative ${
                      isScrolled
                        ? isActive
                          ? "text-[#151a1a]"
                          : "text-[#151a1a]/70 hover:text-[#151a1a]"
                        : isActive
                        ? "text-white"
                        : "text-white/80 hover:text-white"
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-current" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Right side - CTAs */}
            <div className="flex items-center gap-4">
              {/* Wallet Connect Button */}
              <div className="hidden sm:block">
                <WalletMultiButton className="!bg-transparent !border !rounded-lg !backdrop-blur-sm !transition-all !font-inter !text-sm !font-medium !h-auto !py-2 !px-4" />
              </div>
              
              {/* Profile Dropdown (when authenticated) or Import Key Button (when not authenticated) */}
              {isAuthenticated ? (
                <div className="hidden sm:block relative profile-dropdown-container">
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg backdrop-blur-sm transition-all text-xs font-inter font-medium cursor-pointer ${
                      isScrolled
                        ? "bg-[#4878ff]/20 border border-[#4878ff]/30 text-[#4878ff] hover:bg-[#4878ff]/30"
                        : "bg-[#4878ff]/20 border border-[#4878ff]/30 text-[#4878ff] hover:bg-[#4878ff]/30"
                    }`}
                    title={`Profile: ${user?.email || 'User'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Profile</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isProfileDropdownOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isProfileDropdownOpen && (
                    <div className={`absolute right-0 mt-2 w-56 rounded-lg shadow-lg backdrop-blur-md border z-50 ${
                      isScrolled
                        ? "bg-white/95 border-[#151a1a]/20"
                        : "bg-[#0a0e1a]/95 border-white/20"
                    }`}>
                      <div className={`py-2 px-4 border-b ${
                        isScrolled ? "border-[#151a1a]/10" : "border-white/10"
                      }`}>
                        <p className={`text-sm font-medium ${
                          isScrolled ? "text-[#151a1a]" : "text-white"
                        }`}>
                          {user?.email || 'User'}
                        </p>
                        <p className={`text-xs mt-1 ${
                          isScrolled ? "text-[#151a1a]/60" : "text-white/60"
                        }`}>
                          {isUnlocked ? 'Vault unlocked' : 'Vault locked'}
                        </p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            setShowWalletManager(true);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            isScrolled
                              ? "text-[#151a1a]/80 hover:bg-[#151a1a]/10"
                              : "text-white/80 hover:bg-white/10"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                            </svg>
                            <span>Manage Wallets</span>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            setShowWalletManager(true);
                            setWalletManagerMode('import');
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            isScrolled
                              ? "text-[#151a1a]/80 hover:bg-[#151a1a]/10"
                              : "text-white/80 hover:bg-white/10"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span>Import Wallet</span>
                          </div>
                        </button>
                        <button
                          onClick={handleSignOutClick}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            isScrolled
                              ? "text-red-600 hover:bg-red-50"
                              : "text-red-400 hover:bg-red-500/10"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>Sign Out</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hidden sm:block relative">
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg backdrop-blur-sm transition-all text-xs font-inter font-medium cursor-pointer ${
                      isScrolled
                        ? "bg-[#151a1a]/10 border border-[#151a1a]/20 text-[#151a1a]/70 hover:text-[#151a1a] hover:bg-[#151a1a]/15"
                        : "bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/15"
                    }`}
                    title="Sign in with email"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Sign In</span>
                  </button>
                </div>
              )}
              
              {/* Search Button/Input */}
              <div className="relative">
                {!isSearchOpen ? (
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg backdrop-blur-sm transition-all ${
                      isScrolled
                        ? "bg-[#151a1a]/10 border border-[#151a1a]/20 text-[#151a1a]/70 hover:text-[#151a1a] hover:bg-[#151a1a]/15"
                        : "bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/15"
                    }`}
                    aria-label="Search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="text-sm font-inter font-medium">Search CA</span>
                  </button>
                ) : (
                  <div className="hidden sm:flex items-center gap-2">
                    <input
                      type="text"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onBlur={() => {
                        if (!searchValue.trim()) {
                          setTimeout(() => setIsSearchOpen(false), 200);
                        }
                      }}
                      placeholder="Search CA..."
                      disabled={isSearching}
                      autoFocus
                      className={`px-4 py-2 rounded-lg backdrop-blur-sm border transition-all w-64 focus:outline-none focus:ring-2 ${
                        isScrolled
                          ? "bg-white/95 border-[#151a1a]/20 text-[#151a1a] focus:ring-[#4878ff] focus:border-[#4878ff]"
                          : "bg-white/90 border-white/20 text-[#151a1a] focus:ring-[#4878ff] focus:border-[#4878ff]"
                      } disabled:opacity-50`}
                    />
                    <button
                      onClick={() => {
                        setIsSearchOpen(false);
                        setSearchValue('');
                      }}
                      className={`p-2 rounded-lg transition-all ${
                        isScrolled ? "text-[#151a1a]/70 hover:bg-[#151a1a]/10" : "text-white/70 hover:bg-white/10"
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Status indicator */}
              <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm transition-all ${
                isScrolled
                  ? "bg-[#151a1a]/10 border border-[#151a1a]/20"
                  : "bg-white/10 border border-white/20"
              }`}>
                <div className="w-2 h-2 rounded-full bg-[#10b981] animate-pulse" />
                <span className={`text-xs font-inter font-medium transition-colors ${
                  isScrolled ? "text-[#151a1a]/90" : "text-white/90"
                }`}>
                  Live
                </span>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`lg:hidden p-2 transition-colors ${
                  isScrolled ? "text-[#151a1a]" : "text-white"
                }`}
                aria-label="Toggle menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMenuOpen ? (
                    <path d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`lg:hidden backdrop-blur-md border-t transition-all ${
              isScrolled
                ? "bg-white/95 border-[#151a1a]/10"
                : "bg-white/10 border-white/10"
            }`}
          >
            <div className="px-6 py-4 space-y-4">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block text-base font-inter font-medium transition-colors ${
                      isScrolled
                        ? isActive
                          ? "text-[#151a1a]"
                          : "text-[#151a1a]/70 hover:text-[#151a1a]"
                        : isActive
                        ? "text-white"
                        : "text-white/70 hover:text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      {/* Auth Modal - OTP Sign-in and Passphrase Setup */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />

      {/* Private Key Import Modal - Only shown for authenticated users with unlocked vault */}
      {showPrivateKeyModal && isAuthenticated && isUnlocked && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-[#0a0e1a] p-6 rounded-lg shadow-lg border border-[rgba(255,255,255,0.1)] w-full max-w-md mx-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Import Wallet Key</h3>
              <button
                onClick={() => {
                  setShowPrivateKeyModal(false);
                  setPrivateKeyInput("");
                  setKeyStatusMessage(null);
                }}
                className="text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Private Key Input */}
            <div className="space-y-3">
              <p className="text-sm text-white/70">
                Import your wallet's private key to store it encrypted in your vault.
                  </p>
              <textarea
                    value={privateKeyInput}
                    onChange={(e) => setPrivateKeyInput(e.target.value)}
                placeholder="Paste your private key here (base58 encoded)"
                className="w-full bg-white/10 text-white p-3 rounded-md focus:outline-none border border-[rgba(255,255,255,0.1)] focus:border-[#4878ff] focus:ring-2 focus:ring-[#4878ff]/20 placeholder-white/40 min-h-[100px] font-mono text-xs"
              />
                {keyStatusMessage && (
                  <div className={`p-3 rounded-md text-sm ${
                    keyStatusMessage.type === "success"
                      ? "bg-green-500/20 text-green-400 border border-green-500/30"
                      : "bg-red-500/20 text-red-400 border border-red-500/30"
                  }`}>
                    {keyStatusMessage.text}
                  </div>
                )}
                  <button
                onClick={handleImportPrivateKey}
                disabled={isImportingKey || !privateKeyInput.trim()}
                className={`w-full px-4 py-2 rounded-md transition-colors ${
                  isImportingKey || !privateKeyInput.trim()
                        ? "bg-blue-500/50 text-white/70 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                {isImportingKey ? "Importing..." : "Import Private Key"}
                  </button>
                </div>
          </motion.div>
        </div>
      )}

      {/* Wallet Manager Modal */}
      <WalletManager
        isOpen={showWalletManager}
        onClose={() => {
          setShowWalletManager(false);
          setWalletManagerMode('manage');
        }}
        mode={walletManagerMode}
        onRequestUnlock={() => {
          setShowAuthModal(true);
        }}
      />
    </motion.header>
  );
};

export default TransparentHeader;

